import type { ApiResponse, Paginated } from '#shared/types';

/**
 * Relative base URL — the API and the SPA are the same origin, so there is no
 * host to configure. An absolute host would make requests cross-origin in
 * deployment, and the auth cookie would not be sent.
 */
export const API_BASE = '/api/v1';

/** Thrown for any non-2xx response, carrying the server's error envelope. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Field-level validation messages, keyed by field name. */
    readonly fields?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** First message for a field, for rendering next to an input. */
  fieldError(name: string): string | undefined {
    return this.fields?.[name]?.[0];
  }
}

/** Invoked on 401 so the app can redirect to the login screen. */
let onUnauthorized: (() => void) | undefined;
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/**
 * Invoked on 403: the server refused something the screen offered, which
 * usually means an admin changed this person's permissions mid-session. The
 * app re-reads `/auth/me` so the screen stops offering it.
 */
let onForbidden: (() => void) | undefined;
export function setForbiddenHandler(handler: () => void): void {
  onForbidden = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE' | undefined;
  body?: unknown;
  query?: Record<string, string | number | undefined> | undefined;
  signal?: AbortSignal | undefined;
}

function buildUrl(path: string, query: RequestOptions['query']): URL {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }
  return url;
}

function buildInit({ method = 'GET', body, signal }: RequestOptions): RequestInit {
  // `FormData` must go through untouched, and — critically — **without** a
  // Content-Type header. Setting one omits the multipart boundary the browser
  // would otherwise generate, and the server then parses zero parts and reports
  // a missing file for an upload that was plainly there.
  const isMultipart = body instanceof FormData;

  return {
    method,
    // Same-origin, so the auth cookie rides along automatically.
    credentials: 'same-origin',
    ...(body !== undefined && !isMultipart && { headers: { 'Content-Type': 'application/json' } }),
    ...(body !== undefined && { body: isMultipart ? body : JSON.stringify(body) }),
    ...(signal && { signal }),
  };
}

/**
 * The body may not be JSON (e.g. an HTML error page from a proxy); parsing
 * failure must not mask the HTTP status with an opaque SyntaxError.
 */
async function parseEnvelope<T>(response: Response): Promise<ApiResponse<T> | undefined> {
  try {
    return (await response.json()) as ApiResponse<T>;
  } catch {
    return undefined;
  }
}

/** Maps a failed response to an `ApiError`, notifying the app first on 401. */
function toApiError<T>(response: Response, payload: ApiResponse<T> | undefined): ApiError {
  if (response.status === 401) onUnauthorized?.();
  if (response.status === 403) onForbidden?.();
  const error = payload && !payload.success ? payload.data : undefined;
  return new ApiError(
    response.status,
    error?.msg ?? `Request failed with status ${response.status}`,
    error?.fields,
  );
}

/**
 * A binary response — a rendered PDF. Failures still arrive as the JSON error
 * envelope, so they get the same `ApiError` and 401 handling as `request`.
 */
async function requestBlob(path: string, options: RequestOptions): Promise<Blob> {
  const response = await fetch(buildUrl(path, options.query), buildInit(options));
  if (!response.ok) throw toApiError(response, await parseEnvelope<never>(response));
  return response.blob();
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(buildUrl(path, options.query), buildInit(options));
  const payload = await parseEnvelope<T>(response);
  if (!response.ok || !payload?.success) throw toApiError(response, payload);
  return payload.data;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /**
   * A multipart upload.
   *
   * The one endpoint in the app that does not send JSON. Separate from `post`
   * so the `FormData` case is a deliberate choice at the call site rather than
   * something `post` might do depending on what it was handed.
   */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),

  /** POSTs JSON and returns the response body as a blob, e.g. a PDF preview. */
  postForBlob: (path: string, body: unknown, signal?: AbortSignal) =>
    requestBlob(path, { method: 'POST', body, signal }),

  /** List endpoints all return `{ count, items }`. */
  list: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<Paginated<T>>(path, { query, signal }),
};
